import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@produzionejb/ui';
import type { PscListItem, LnpListItem, BulkCloseType } from '@produzionejb/types';
import { usePscList } from '../../../lib/psc-api';
import { useLnpList } from '../../../lib/lnp-api';
import { useBulkClose } from '../../../lib/documents-bulk-api';
import { ApiError } from '../../../lib/api-client';

type DocItem = PscListItem | LnpListItem;

function DocTable({
  rows,
  selected,
  toggle,
  hrefBase,
}: {
  rows: DocItem[];
  selected: Set<string>;
  toggle: (id: string) => void;
  hrefBase: string;
}) {
  const { t } = useTranslation();
  if (rows.length === 0) {
    return <p className="text-xs text-slate-400">{t('dashboard.docOpenPscLnp.empty')}</p>;
  }
  return (
    <table className="w-full text-xs">
      <thead className="border-b border-slate-200 bg-slate-50">
        <tr>
          <th className="w-6 px-1 py-1"></th>
          <th className="px-2 py-1 text-left font-medium text-slate-600">
            {t('dashboard.docOpenPscLnp.col.name')}
          </th>
          <th className="px-2 py-1 text-left font-medium text-slate-600">
            {t('dashboard.docOpenPscLnp.col.workDate')}
          </th>
          <th className="px-2 py-1 text-left font-medium text-slate-600">
            {t('dashboard.docOpenPscLnp.col.responsable')}
          </th>
          <th className="px-2 py-1 text-right font-medium text-slate-600">
            {t('dashboard.docOpenPscLnp.col.sfrido')}
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((d) => (
          <tr key={d.id} className="border-b border-slate-100 last:border-0">
            <td className="px-1 py-1 text-center">
              <input
                type="checkbox"
                checked={selected.has(d.id)}
                onChange={() => toggle(d.id)}
                aria-label={`select-${d.id}`}
              />
            </td>
            <td className="px-2 py-1">
              <Link to={`${hrefBase}/${d.id}/edit`} className="text-brand-700 hover:underline">
                {d.draftName ?? d.code ?? d.id.slice(0, 8)}
              </Link>
            </td>
            <td className="px-2 py-1 text-slate-600">
              {new Date(d.workDate).toLocaleDateString('it-IT')}
            </td>
            <td className="px-2 py-1 text-slate-600">{d.responsable ?? '—'}</td>
            <td className="px-2 py-1 text-right font-mono">
              {Number(d.sfridoG).toFixed(0)}g ({d.sfridoPct.toFixed(1)}%)
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function DocOpenPscLnpWidget() {
  const { t } = useTranslation();
  const pscQ = usePscList({ status: 'DRAFT', pageSize: 50 });
  const lnpQ = useLnpList({ status: 'DRAFT', pageSize: 50 });
  const bulkCloseMut = useBulkClose();

  const [selectedPsc, setSelectedPsc] = useState<Set<string>>(new Set());
  const [selectedLnp, setSelectedLnp] = useState<Set<string>>(new Set());

  const togglePsc = (id: string) => {
    setSelectedPsc((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleLnp = (id: string) => {
    setSelectedLnp((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const pscRows = pscQ.data?.items ?? [];
  const lnpRows = lnpQ.data?.items ?? [];

  const totalSelected = selectedPsc.size + selectedLnp.size;

  async function handleBulkClose(type: BulkCloseType) {
    const ids = type === 'PSC' ? Array.from(selectedPsc) : Array.from(selectedLnp);
    if (ids.length === 0) return;
    const msg = t('dashboard.docOpenPscLnp.confirmBulkClose', { count: ids.length, type });
    if (!window.confirm(msg)) return;

    try {
      const r = await bulkCloseMut.mutateAsync({ type, ids });
      window.alert(t('dashboard.docOpenPscLnp.bulkSuccess', { count: r.closed, type }));
      if (type === 'PSC') setSelectedPsc(new Set());
      else setSelectedLnp(new Set());
    } catch (err) {
      const reason =
        err instanceof ApiError && err.payload && typeof err.payload === 'object'
          ? JSON.stringify(err.payload)
          : (err as Error).message;
      window.alert(`${t('dashboard.docOpenPscLnp.bulkError')}: ${reason}`);
    }
  }

  const isLoading = pscQ.isLoading || lnpQ.isLoading;

  // Keep memoized counts to avoid resets
  const counts = useMemo(
    () => ({ psc: pscRows.length, lnp: lnpRows.length }),
    [pscRows.length, lnpRows.length],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>{t('dashboard.docOpenPscLnp.title')}</span>
          {totalSelected > 0 && (
            <span className="text-sm font-normal text-slate-500">
              {t('dashboard.docOpenPscLnp.selectedCount', { count: totalSelected })}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading && <p className="text-sm text-slate-500">{t('common.loading')}</p>}

        {!isLoading && (
          <>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                  PSC <span className="text-slate-400">({counts.psc})</span>
                </p>
                {selectedPsc.size > 0 && (
                  <Button
                    size="sm"
                    onClick={() => handleBulkClose('PSC')}
                    disabled={bulkCloseMut.isPending}
                  >
                    {t('dashboard.docOpenPscLnp.bulkCloseButton', { count: selectedPsc.size })}
                  </Button>
                )}
              </div>
              <DocTable
                rows={pscRows}
                selected={selectedPsc}
                toggle={togglePsc}
                hrefBase="/documents/psc"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                  LNP <span className="text-slate-400">({counts.lnp})</span>
                </p>
                {selectedLnp.size > 0 && (
                  <Button
                    size="sm"
                    onClick={() => handleBulkClose('LNP')}
                    disabled={bulkCloseMut.isPending}
                  >
                    {t('dashboard.docOpenPscLnp.bulkCloseButton', { count: selectedLnp.size })}
                  </Button>
                )}
              </div>
              <DocTable
                rows={lnpRows}
                selected={selectedLnp}
                toggle={toggleLnp}
                hrefBase="/documents/lnp"
              />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
